import { jsx as _jsx } from "react/jsx-runtime";
/* eslint-disable react-refresh/only-export-components */
import { createContext, useCallback, useContext, useMemo, } from 'react';
import { useGraphInbox } from '@/hooks/useGraphInbox';
import { logAudit } from '@/services/audit/logAudit';
const CommunicationsContext = createContext(undefined);
export function CommunicationsProvider({ children }) {
    const { communications, setCommunications, loading, error, refresh, } = useGraphInbox();
    const patchCommunication = useCallback((id, patch) => {
        const ts = new Date().toISOString();
        setCommunications((prev) => prev.map((c) => c.graphMessageId === id
            ? {
                ...c,
                ...patch,
                lastActionAt: ts,
            }
            : c));
    }, [setCommunications]);
    const confirmClassification = useCallback((id) => {
        const comm = communications.find((c) => c.graphMessageId === id);
        if (!comm)
            return;
        const ts = new Date().toISOString();
        patchCommunication(id, {
            validationStatus: 'validated',
            validatedAt: ts,
            validatedCategory: comm.category,
        });
        logAudit('CONFIRM_CLASSIFICATION', {
            graphMessageId: comm.graphMessageId,
            internetMessageId: comm.internetMessageId,
            subject: comm.subject,
            category: comm.category,
            validationStatus: 'validated',
            timestamp: ts,
        });
    }, [communications, patchCommunication]);
    const changeCategory = useCallback((id, category) => {
        const comm = communications.find((c) => c.graphMessageId === id);
        if (!comm)
            return;
        const ts = new Date().toISOString();
        const previousCategory = comm.category;
        patchCommunication(id, {
            validationStatus: 'validated',
            validatedAt: ts,
            validatedCategory: category,
            category,
            categories: [
                category,
                ...(comm.categories || []).filter((c) => c !== category),
            ],
        });
        logAudit('CHANGE_CATEGORY', {
            graphMessageId: comm.graphMessageId,
            internetMessageId: comm.internetMessageId,
            subject: comm.subject,
            category,
            previousCategory,
            validationStatus: 'validated',
            timestamp: ts,
        });
    }, [communications, patchCommunication]);
    const escalate = useCallback((id) => {
        const comm = communications.find((c) => c.graphMessageId === id);
        if (!comm)
            return;
        const ts = new Date().toISOString();
        patchCommunication(id, {
            validationStatus: 'escalated',
            validatedAt: comm.validatedAt,
            validatedCategory: comm.validatedCategory,
        });
        logAudit('ESCALATE_SUPERVISOR', {
            graphMessageId: comm.graphMessageId,
            internetMessageId: comm.internetMessageId,
            subject: comm.subject,
            category: comm.category,
            validationStatus: 'escalated',
            timestamp: ts,
        });
    }, [communications, patchCommunication]);
    const value = useMemo(() => ({
        communications,
        loading,
        error,
        refresh,
        confirmClassification,
        changeCategory,
        escalate,
    }), [
        communications,
        loading,
        error,
        refresh,
        confirmClassification,
        changeCategory,
        escalate,
    ]);
    return (_jsx(CommunicationsContext.Provider, { value: value, children: children }));
}
export function useCommunications() {
    const ctx = useContext(CommunicationsContext);
    if (!ctx) {
        throw new Error('useCommunications must be used within CommunicationsProvider');
    }
    return ctx;
}
